// Navigation Section Component
// Collapsible section with header and navigation items

import React from 'react';
import { useTheme } from '../../design-system/ThemeProvider';
import { NavigationIcon } from './NavigationIcon';
import { NavigationItem } from './NavigationItem';
import { NavigationSection as NavigationSectionType } from './types';

interface NavigationSectionProps {
  section: NavigationSectionType;
  isExpanded: boolean;
  activeItemId: string | null;
  onToggleSection: () => void;
  onItemClick: (itemId: string, path?: string) => void;
  onItemToggle: (itemId: string) => void;
  expandedItems: Set<string>;
}

export const NavigationSection: React.FC<NavigationSectionProps> = ({
  section,
  isExpanded,
  activeItemId,
  onToggleSection,
  onItemClick,
  onItemToggle,
  expandedItems
}) => {
  const { theme } = useTheme();

  const sectionHeaderStyles = {
    display: 'flex',
    alignItems: 'center',
    padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
    marginBottom: theme.componentTokens.navigation.section.headerMarginBottom,
    cursor: section.isCollapsible ? 'pointer' : 'default',
    fontSize: theme.componentTokens.navigation.section.headerFontSize,
    fontWeight: theme.componentTokens.navigation.section.headerFontWeight,
    color: theme.componentTokens.navigation.section.headerText,
    textTransform: 'uppercase' as const,
    letterSpacing: theme.componentTokens.navigation.section.headerLetterSpacing,
    transition: theme.transitions.duration.fast
  };

  const sectionContentStyles = {
    overflow: 'hidden',
    transition: `max-height ${theme.transitions.duration.normal} ${theme.transitions.easing.ease}`,
    maxHeight: isExpanded ? '1000px' : '0'
  };

  const handleSectionClick = () => {
    if (section.isCollapsible) {
      onToggleSection();
    }
  };

  const handleSectionMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (section.isCollapsible) {
      e.currentTarget.style.color = theme.semanticColors.text.primary;
    }
  };

  const handleSectionMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    if (section.isCollapsible) {
      e.currentTarget.style.color = theme.componentTokens.navigation.section.headerText;
    }
  };

  return (
    <div style={{ marginBottom: theme.componentTokens.navigation.section.marginBottom }}>
      {/* Section Header */}
      <div
        style={sectionHeaderStyles}
        onClick={handleSectionClick}
        onMouseEnter={handleSectionMouseEnter}
        onMouseLeave={handleSectionMouseLeave}
        role={section.isCollapsible ? 'button' : undefined}
        tabIndex={section.isCollapsible ? 0 : undefined}
        aria-expanded={section.isCollapsible ? isExpanded : undefined}
      >
        <span style={{ flex: 1 }}>{section.label}</span>
        {section.isCollapsible && (
          <NavigationIcon
            name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
            size={14}
            color="currentColor"
          />
        )}
      </div>

      {/* Section Items */}
      <div style={sectionContentStyles}>
        {section.items.map((item) => {
          const hasChildren = item.children && item.children.length > 0;
          const isItemExpanded = expandedItems.has(item.id);
          
          return (
            <div key={item.id}>
              <NavigationItem
                item={item}
                isActive={activeItemId === item.id}
                isExpanded={isItemExpanded}
                hasChildren={hasChildren}
                onClick={() => onItemClick(item.id, item.path)}
                onToggle={() => onItemToggle(item.id)}
              />
              
              {/* Render children if expanded */}
              {hasChildren && isItemExpanded && (
                <div style={{ marginLeft: theme.spacing[4] }}>
                  {item.children!.map((child) => (
                    <NavigationItem
                      key={child.id}
                      item={child}
                      isActive={activeItemId === child.id}
                      depth={1}
                      onClick={() => onItemClick(child.id, child.path)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};